import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/utils/dateUtils';

interface SplitAlertPayloadProps {
  payload: Record<string, unknown>;
  i18n: {
    _: (d: { id: string; message: string; values?: Record<string, unknown> }) => string;
  };
}

/**
 * Local copy of WorkflowPayloadDetail's FieldRow. This is a detail panel, not a
 * data table, so BaseEditableTable does not apply and no raw table element is used.
 */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Read a payload field as a non-blank string, or undefined. */
function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/** Read a payload field as a finite number, or undefined. */
function readNumber(payload: Record<string, unknown>, key: string): number | undefined {
  const value = payload[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Read a payload field as an array of non-blank strings (non-strings dropped). */
function readStringArray(payload: Record<string, unknown>, key: string): string[] {
  const value = payload[key];
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim() !== '');
}

/** Show a weighted number with at most two decimals. */
function formatWeighted(value: number): string {
  return String(Math.round(value * 100) / 100);
}

const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;

/**
 * Renders a `split_alert` workflow notice (issue #1594): a combined group unit
 * went over the host group's capacity or was short of staff after a check-in.
 * Rooms and counts only -- the payload carries no child or employee.
 *
 * window_start / window_end are org-local RFC 3339 timestamps. The date and the
 * clock times are read straight from the strings (never through new Date) so the
 * browser's timezone cannot shift them.
 */
export function SplitAlertPayload({ payload, i18n }: SplitAlertPayloadProps) {
  const hostGroupName = readString(payload, 'host_group_name');
  const memberGroupNames = readStringArray(payload, 'member_group_names');
  const windowStart = readString(payload, 'window_start');
  const windowEnd = readString(payload, 'window_end');
  const reason = readString(payload, 'reason');
  const presentChildren = readNumber(payload, 'present_children') ?? 0;
  const presentWeighted = readNumber(payload, 'present_weighted') ?? 0;
  const hostCapacity = readNumber(payload, 'host_capacity');
  const hostCapacityWeighted = readNumber(payload, 'host_capacity_weighted');
  const requiredStaff = readNumber(payload, 'required_staff') ?? 0;
  const scheduledStaff = readNumber(payload, 'scheduled_staff') ?? 0;
  const minQualifiedStaff = readNumber(payload, 'min_qualified_staff') ?? 0;
  const scheduledQualifiedStaff = readNumber(payload, 'scheduled_qualified_staff') ?? 0;

  const windowDate =
    windowStart && DATE_PREFIX.test(windowStart) ? windowStart.slice(0, 10) : undefined;
  const startTime = windowStart && windowStart.length >= 16 ? windowStart.slice(11, 16) : '';
  const endTime = windowEnd && windowEnd.length >= 16 ? windowEnd.slice(11, 16) : '';

  const reasonLabel = (() => {
    switch (reason) {
      case undefined:
        return undefined;
      case 'OVER_CAPACITY':
        return i18n._({ id: 'workflow.payload.splitAlert.reason.overCapacity', message: 'Over capacity' });
      case 'SHORT_STAFF':
        return i18n._({ id: 'workflow.payload.splitAlert.reason.shortStaff', message: 'Short of staff' });
      case 'BOTH':
        return i18n._({
          id: 'workflow.payload.splitAlert.reason.both',
          message: 'Over capacity and short of staff',
        });
      default:
        return reason;
    }
  })();

  // A weighted capacity is compared with the weighted load, never the headcount.
  const childrenLabel =
    hostCapacityWeighted !== undefined
      ? i18n._({
          id: 'workflow.payload.splitAlert.childrenWeighted',
          message: '{present} children, weighted load {weighted}, capacity {capacity}',
          values: {
            present: presentChildren,
            weighted: formatWeighted(presentWeighted),
            capacity: formatWeighted(hostCapacityWeighted),
          },
        })
      : hostCapacity !== undefined
        ? i18n._({
            id: 'workflow.payload.splitAlert.childrenOfCapacity',
            message: '{present} children, capacity {capacity}',
            values: { present: presentChildren, capacity: hostCapacity },
          })
        : i18n._({
            id: 'workflow.payload.splitAlert.childrenNoCapacity',
            message: '{present} children',
            values: { present: presentChildren },
          });

  return (
    <div className="space-y-4">
      {hostGroupName && (
        <FieldRow label={i18n._({ id: 'workflow.payload.splitAlert.unit', message: 'Unit' })}>
          <p className="text-sm">{[hostGroupName, ...memberGroupNames].join(' + ')}</p>
        </FieldRow>
      )}

      {windowDate && (
        <FieldRow label={i18n._({ id: 'workflow.payload.splitAlert.window', message: 'Window' })}>
          <p className="text-sm">
            {i18n._({
              id: 'workflow.payload.splitAlert.windowValue',
              message: '{date}, {start} to {end}',
              values: { date: formatDate(windowDate), start: startTime, end: endTime },
            })}
          </p>
        </FieldRow>
      )}

      {reasonLabel && (
        <FieldRow label={i18n._({ id: 'workflow.payload.splitAlert.reason', message: 'Reason' })}>
          {/* Text-bearing badge, so the reason is never colour-only. */}
          <Badge variant="outline">{reasonLabel}</Badge>
        </FieldRow>
      )}

      <FieldRow label={i18n._({ id: 'workflow.payload.splitAlert.children', message: 'Children' })}>
        <p className="text-sm">{childrenLabel}</p>
      </FieldRow>

      <FieldRow label={i18n._({ id: 'workflow.payload.splitAlert.staff', message: 'Staff' })}>
        <p className="text-sm">
          {i18n._({
            id: 'workflow.payload.splitAlert.staffOfRequired',
            message: '{scheduled} of {required} staff',
            values: { scheduled: scheduledStaff, required: requiredStaff },
          })}
        </p>
        {minQualifiedStaff > 0 && (
          <p className="text-sm">
            {i18n._({
              id: 'workflow.payload.splitAlert.qualifiedOfRequired',
              message: '{scheduled} of {required} qualified staff',
              values: { scheduled: scheduledQualifiedStaff, required: minQualifiedStaff },
            })}
          </p>
        )}
      </FieldRow>

      {windowDate && (
        <Link
          to={`/shifts?tab=shifts&date=${windowDate}&view=day`}
          className="text-sm text-primary underline underline-offset-4"
        >
          {i18n._({
            id: 'workflow.payload.splitAlert.openPlanner',
            message: 'Open shift planner for this day',
          })}
        </Link>
      )}
    </div>
  );
}
